import { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Modal,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Chip,
  Checkbox,
  ListItemText,
  ListSubheader,
} from '@mui/material';
import { jids } from '../../../data/forms/generalDropdownOptions.js';
import { oemVendors } from '../../../data/forms/depot/benchRepairDropdownOptions.js';
import { useAuth } from '../../../context/authContext.js';
import axios from 'axios';

export default function PackageModal({
  isOpen,
  handleClose,
  repairOrder,
  isPackageEdit,
  handleSearch,
  currentPackage,
  setSnackbarMessage,
  setSnackbarSeverity,
  setIsSnackbarOpen,
}) {
  const [entries, setEntries] = useState([]);
  const [location, setLocation] = useState('');
  const [shipStatus, setShipStatus] = useState('');
  const [courier, setCourier] = useState('');
  const [trackingNum, setTrackingNum] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { user } = useAuth();

  useEffect(() => {
    if (currentPackage !== null) {
      setLocation(currentPackage.location);
      setShipStatus(currentPackage.currentStatus);
      setCourier(currentPackage.courier);
      setTrackingNum(currentPackage.trackingNumber);

      let currentEntries = [];

      repairOrder.entries.forEach(entry => {
        if (
          currentPackage.contents &&
          currentPackage.contents.includes(entry.id)
        ) {
          currentEntries.push(entry);
        }
      });

      setEntries(currentEntries);
    }
  }, [currentPackage, repairOrder]);

  function handleEntryChange(e) {
    setEntries(
      typeof e.target.value === 'string'
        ? e.target.value.split(',')
        : e.target.value
    );

    if (
      repairOrder.repairOrderDetails.oem === 'Yes' &&
      e.target.value.length > 0
    ) {
      setLocation(e.target.value[0].oemVendor);
    }
  }

  function handleLocationChange(e) {
    setLocation(e.target.value);
  }

  function handleShipStatusChange(e) {
    if (e.target.value === 'Not Shipped') {
      setCourier('');
      setTrackingNum('');
    }
    setShipStatus(e.target.value);
  }

  function handleCourierChange(e) {
    setCourier(e.target.value);
  }

  function handleTrackingNumChange(e) {
    setTrackingNum(e.target.value);
  }

  // Validation logic
  const isFormValid = () => {
    if (shipStatus !== 'Not Shipped') {
      return (
        entries.length !== 0 &&
        location !== '' &&
        shipStatus !== '' &&
        trackingNum !== ''
      );
    } else {
      return entries.length !== 0 && location !== '' && shipStatus !== '';
    }
  };

  async function submitUpdate() {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'PUT',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/depot/benchrepair/updatePackage`,
        data: {
          entryId: repairOrder.repairOrderDetails.entryId,
          packageId: currentPackage.id,
          currentStatus: shipStatus,
          location: location,
          courier: courier,
          trackingNumber: trackingNum,
          userId: user.userId,
        },
      });
      setSnackbarMessage('Package updated successfully');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClose();
      handleSearch(repairOrder.repairOrderDetails.repairOrder, 'package');
    } catch (error) {
      console.error('Error updating package:', error);
      setSnackbarMessage(
        error.response?.data?.message ||
          'Error updating package. Please try again. If the problem persists, contact support.'
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
    }
  }

  function createEntriesPayload(entries) {
    let entriesIds = [];
    entries.forEach(entry => entriesIds.push(entry.id));
    return entriesIds;
  }

  async function submitNew() {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/depot/benchrepair/newPackage`,
        data: {
          entryId: repairOrder.repairOrderDetails.entryId,
          entries: createEntriesPayload(entries),
          location: location,
          currentStatus: shipStatus,
          courier: courier,
          trackingNumber: trackingNum,
          userId: user.userId,
        },
      });
      setSnackbarMessage('Package created successfully');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClose();
      handleSearch(repairOrder.repairOrderDetails.repairOrder, 'package');
    } catch (error) {
      console.error('Error creating package:', error);
      setSnackbarMessage(
        error.response?.data?.message ||
          'Error creating package. Please try again. If the problem persists, contact support.'
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  }

  const isDelivered = entry => {
    let result = true;
    repairOrder.packages.forEach(pkg => {
      if (pkg.contents) {
        const parsedContents = JSON.parse(pkg.contents);
        if (parsedContents.includes(entry.id)) {
          if (pkg.currentStatus !== 'Delivered') {
            result = false;
          }
        }
      }
    });

    return result;
  };

  const canShip = repairOrder.entries.find(
    entry => entry.packageId === null || isDelivered(entry)
  );

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      {canShip || isPackageEdit ? (
        <Grid
          container
          spacing={2}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'var(--dust-white)',
            p: 4,
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          <Grid size={12}>
            <Typography
              id='modal-modal-title'
              sx={{ fontFamily: 'BrightstarSans-Regular', fontSize: '25px' }}
            >
              {isPackageEdit ? 'Edit Package Details' : 'Create Package'}
            </Typography>
          </Grid>
          <Grid size={12}>
            <FormControl
              required
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            >
              <InputLabel>Entry</InputLabel>
              <Select
                value={entries}
                defaultValue={''}
                onChange={handleEntryChange}
                disabled={isPackageEdit}
                multiple
                input={
                  <OutlinedInput id='select-multiple-chip' label='Entry' />
                }
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(value => (
                      <Chip
                        key={value.id}
                        label={`${value.product} (${value.serialNumber})`}
                      />
                    ))}
                  </Box>
                )}
              >
                {repairOrder.entries.map(
                  entry =>
                    isDelivered(entry) && (
                      <MenuItem value={entry}>
                        <Checkbox checked={entries.includes(entry)} />
                        <ListItemText
                          primary={`${entry.product} (S/N: ${entry.serialNumber})`}
                        />
                      </MenuItem>
                    )
                )}
              </Select>
            </FormControl>
            <FormControl
              required
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            >
              <InputLabel>Destination</InputLabel>
              <Select
                label={'Destination'}
                value={location}
                defaultValue={''}
                onChange={handleLocationChange}
              >
                <ListSubheader>Jurisdictions</ListSubheader>
                {jids.map(jid => (
                  <MenuItem value={jid}>{jid}</MenuItem>
                ))}
                <ListSubheader>OEM Vendors</ListSubheader>
                {oemVendors.map(vendor => (
                  <MenuItem value={vendor}>{vendor}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              required
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            >
              <InputLabel>Shipping Status</InputLabel>
              <Select
                value={shipStatus}
                label={'Shipping Status'}
                defaultValue={''}
                onChange={handleShipStatusChange}
              >
                <MenuItem value={'Not Shipped'}>Not Shipped</MenuItem>
                <MenuItem value={'In Transit'}>In Transit</MenuItem>
                <MenuItem value={'Delivered'}>Delivered</MenuItem>
              </Select>
            </FormControl>
            {(shipStatus === 'In Transit' || shipStatus === 'Delivered') && (
              <>
                <TextField
                  required
                  id='outlined-required'
                  label='Courier'
                  fullWidth
                  value={courier}
                  onChange={handleCourierChange}
                  sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
                />
                <TextField
                  required
                  id='outlined-required'
                  label='Tracking Number'
                  fullWidth
                  value={trackingNum}
                  onChange={handleTrackingNumChange}
                  sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
                />
              </>
            )}
          </Grid>
          <Grid
            size={12}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Button
              variant='contained'
              sx={{
                marginRight: '10px',
                backgroundColor: 'var(--arcturus-red)',
              }}
              onClick={handleClose}
            >
              Close
            </Button>
            <Button
              disabled={!isFormValid() || isSubmitting}
              variant='contained'
              sx={{ backgroundColor: 'var(--vega-blue)' }}
              onClick={isPackageEdit ? submitUpdate : submitNew}
            >
              {isPackageEdit ? 'Update' : 'Submit'}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          spacing={2}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'var(--dust-white)',
            p: 4,
          }}
        >
          <Grid size={12}>
            <Typography
              id='modal-modal-title'
              sx={{ fontFamily: 'BrightstarSans-Regular', fontSize: '25px' }}
            >
              {'All Entries are in packages or Not Delivered yet'}
            </Typography>
          </Grid>
          <Grid
            size={12}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Button
              variant='contained'
              sx={{
                marginRight: '10px',
                backgroundColor: 'var(--arcturus-red)',
              }}
              onClick={handleClose}
            >
              Close
            </Button>
          </Grid>
        </Grid>
      )}
    </Modal>
  );
}
